
import React from 'react';
import { ArrowLeft, BookOpen, Target, BarChart3, Layers, Calculator, FileText, Users, Droplets, TreePine, Mountain, Thermometer, CloudRain, Database } from 'lucide-react';
import { Link } from 'react-router-dom';

const Metodologia = () => {
  const criterios = [
  {
    nombre: "Vulnerabilidad Física",
    peso: 30,
    indicadores: [
    { nombre: "Pendiente promedio", descripcion: "Grado de inclinación del terreno", unidad: "%" },
    { nombre: "Altitud promedio", descripcion: "Elevación sobre el nivel del mar", unidad: "msnm" },
    { nombre: "Densidad de drenaje", descripcion: "Longitud de cauces por unidad de área", unidad: "km/km²" }],

    icon: Mountain,
    color: "text-red-600"
  },
  {
    nombre: "Vulnerabilidad Climática",
    peso: 25,
    indicadores: [
    { nombre: "Precipitación anual", descripcion: "Cantidad de lluvia promedio anual", unidad: "mm" },
    { nombre: "Temperatura promedio", descripcion: "Temperatura media anual", unidad: "°C" },
    { nombre: "Variabilidad climática", descripcion: "Índice de variación estacional", unidad: "adimensional" }],

    icon: CloudRain,
    color: "text-blue-600"
  },
  {
    nombre: "Vulnerabilidad Ambiental",
    peso: 25,
    indicadores: [
    { nombre: "Cobertura forestal", descripcion: "Porcentaje de área con bosque", unidad: "%" },
    { nombre: "Cobertura agrícola", descripcion: "Porcentaje de área con agrícola", unidad: "categórico" },
    { nombre: "Cobertura Pastizal", descripcion: "Porcentaje de área con pastizal", unidad: "índice" }],

    icon: TreePine,
    color: "text-green-600"
  },
  {
    nombre: "Vulnerabilidad Social",
    peso: 20,
    indicadores: [
    { nombre: "Densidad poblacional", descripcion: "Habitantes por unidad de área", unidad: "hab/km²" },
    { nombre: "Índice de marginación", descripcion: "Nivel socioeconómico", unidad: "índice" },
    { nombre: "Acceso a servicios", descripcion: "Disponibilidad de infraestructura", unidad: "%" }],

    icon: Users,
    color: "text-purple-600"
  }];

  const indicadoresPriorizacion = [
  { indicador: "Fracción de escurrimiento", ponderacion: "0.2475 (24.75%)", campo: "pond_escur", definicion: "Relación entre agua que escurre y agua que se filtra (%)" },
  { indicador: "Erosión", ponderacion: "0.2267 (22.67%)", campo: "eros_erosi", definicion: "Tasa de pérdida de suelo por factores naturales y/o antrópicos (Ton/ha/año)" },
  { indicador: "Tasa de deforestación", ponderacion: "0.1292 (12.92%)", campo: "SUP_DEFORE", definicion: "Deforestación de la microcuenca (Ha)" },
  { indicador: "Uso agropecuario", ponderacion: "0.1292 (12.92%)", campo: "Uso_agropecuario (calculado)", definicion: "Suma de superficie de uso agrícola y pecuario (Porcentaje de superficie)" },
  { indicador: "Incendios", ponderacion: "0.0867 (8.67%)", campo: "Sup_incend", definicion: "Superficie afectada por incendios forestales (Há)" },
  { indicador: "Importancia para la conservación", ponderacion: "0.0608 (6.08%)", campo: "Importancia_conservacion", definicion: "Superficie de la microcuenca, categorizada como área de conservación estatal, federal, privada, etc. (Ha)" },
  { indicador: "Densidad de población", ponderacion: "0.0579 (5.79%)", campo: "Densidad_poblacion", definicion: "Población por unidad de superficie (hab/km2)" },
  { indicador: "Rezago social", ponderacion: "0.0621 (6.21%)", campo: "Rezago_social", definicion: "Índice de rezago social" },
  { indicador: "ÍNDICE DE PRIORIDAD TOTAL", ponderacion: "1.0000 (100%)", campo: "Indice_Prioridad", definicion: "Suma ponderada de todos los indicadores de priorización" }];


  const metodologiaSteps = [
  {
    step: 1,
    title: "Definición de Indicadores",
    description: "Se utilizan 8 indicadores clave con sus respectivas ponderaciones."
  },
  {
    step: 2,
    title: "Definición de Indicadores y Recolección de Datos",
    description: "Obtención de información geoespacial, climática, ambiental y socioeconómica de fuentes oficiales como INEGI, CONABIO, SMN y CONAPO."
  },
  {
    step: 3,
    title: "Normalización de Datos",
    description: "Estandarización de variables mediante transformación Min-Max para valores entre 0 y 1, considerando la dirección del impacto."
  },
  {
    step: 4,
    title: "Índice de Prioridad",
    description: "Aplicación de pesos específicos a cada criterio basados en análisis de expertos y literatura científica especializada."
  },
  {
    step: 5,
    title: "Clasificación",
    description: "Categorización en niveles de prioridad: Muy Alta (0.8-1.0), Alta (0.6-0.8), Media (0.4-0.6), Baja (0.2-0.4), Muy Baja (0.0-0.2)."
  },
  {
    step: 6,
    title: "Validación",
    description: "Verificación de resultados mediante trabajo de campo, consulta con expertos locales y análisis de consistencia espacial."
  }];



  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-green-50 to-blue-100">
      {/* Header */}
      <div className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors">

                <ArrowLeft className="h-5 w-5" />
                <span>Volver al inicio</span>
              </Link>
            </div>
            <div className="flex items-center space-x-3">
              <BookOpen className="h-8 w-8 text-blue-600" />
              <h1 className="text-3xl font-bold text-gray-900">
                Metodología General
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Introducción */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <Target className="h-6 w-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Objetivo y Alcance</h2>
          </div>
          <div className="prose max-w-none text-gray-700">
            <p className="text-lg mb-4">El sistema de priorización de microcuencas en Chiapas tiene como objetivo identificar y jerarquizar las áreas que requieren atención prioritaria para la conservación, restauración y manejo sustentable de los recursos hídricos, considerando específicamente las 79 microcuencas previamente seleccionadas por la Secretaría de Medio Ambiente e Historia Natural (SEMAHN).              de los recursos hídricos.



            </p>
            <p className="mb-4">Para ello, las microcuencas fueron caracterizadas y analizadas de manera integral mediante parámetros geomorfológicos, hidroclimáticos y de uso del suelo, permitiendo comprender sus condiciones físicas y ambientales, así como las presiones existentes sobre el territorio.



            </p>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
              <p className="font-semibold text-blue-800">Marco Conceptual:</p>
              <p className="text-blue-700">La jerarquización se realizó mediante un enfoque multicriterio que integra diversas dimensiones de vulnerabilidad (física, climática, ambiental y social) para obtener un índice de priorización robusto y técnicamente fundamentado. Los criterios considerados y su ponderación fueron definidos por un grupo colegiado integrado por representantes institucionales de gobierno estatal, organizaciones civiles y centros de investigación, garantizando un proceso de evaluación participativo y técnicamente sustentado.


              </p>
            </div>
          </div>
        </div>

        {/* Proceso Metodológico */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <Calculator className="h-6 w-6 text-purple-600" />
            <h2 className="text-2xl font-bold text-gray-900">Proceso Metodológico</h2>
          </div>
          
          <div className="space-y-6">
            {metodologiaSteps.map((step, index) =>
            <div key={index} className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                  {step.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-700">{step.description}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Indicadores de Priorización */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <Database className="h-6 w-6 text-indigo-600" />
            <h2 className="text-2xl font-bold text-gray-900">1. Indicadores de Priorización</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">
                    Indicador
                  </th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">
                    Ponderación
                  </th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">
                    Definición
                  </th>
                </tr>
              </thead>
              <tbody>
                {indicadoresPriorizacion.map((item, index) =>
                <tr key={index} className={`${index === indicadoresPriorizacion.length - 1 ? 'bg-blue-50 font-semibold' : 'hover:bg-gray-50'}`}>
                    <td className="border border-gray-300 px-4 py-3 text-gray-800">
                      {item.indicador}
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-gray-800">
                      {item.ponderacion}
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-gray-800">
                      {item.definicion}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          <div className="mt-4 p-4 bg-indigo-50 border-l-4 border-indigo-400 rounded-r-lg">
            <p className="text-sm text-indigo-700">
              <strong>Nota:</strong> Los indicadores están ordenados por su peso relativo en el cálculo del índice de priorización. 
              El índice final se calcula como la suma ponderada de todos los componentes normalizados.
            </p>
          </div>
        </div>

        {/* Criterios y Variables */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <Layers className="h-6 w-6 text-green-600" />
            <h2 className="text-2xl font-bold text-gray-900">2. Recolección de datos</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gradient-to-r from-blue-600 to-green-600 text-white">
                  <th className="border border-gray-300 px-6 py-4 text-left font-semibold">Indicador</th>
                  <th className="border border-gray-300 px-6 py-4 text-left font-semibold">Fuente / Base de Datos</th>
                  <th className="border border-gray-300 px-6 py-4 text-left font-semibold">Año</th>
                </tr>
              </thead>
              <tbody>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="border border-gray-300 px-6 py-4 font-medium text-gray-900">Fracción de Escurrimiento</td>
                  <td className="border border-gray-300 px-6 py-4 text-gray-700">INEGI / Cartas Hidrológicas</td>
                  <td className="border border-gray-300 px-6 py-4 text-gray-700">2020</td>
                </tr>
                <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                  <td className="border border-gray-300 px-6 py-4 font-medium text-gray-900">Erosión</td>
                  <td className="border border-gray-300 px-6 py-4 text-gray-700">SEMARNAT / Inventario Nacional Forestal</td>
                  <td className="border border-gray-300 px-6 py-4 text-gray-700">2019</td>
                </tr>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="border border-gray-300 px-6 py-4 font-medium text-gray-900">Tasa de Deforestación</td>
                  <td className="border border-gray-300 px-6 py-4 text-gray-700">CONAFOR / Monitoreo de Cambio de Uso de Suelo</td>
                  <td className="border border-gray-300 px-6 py-4 text-gray-700">2018-2021</td>
                </tr>
                <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                  <td className="border border-gray-300 px-6 py-4 font-medium text-gray-900">Uso Agropecuario</td>
                  <td className="border border-gray-300 px-6 py-4 text-gray-700">CONABIO-Uso de suelo y vegetación 1:30,000</td>
                  <td className="border border-gray-300 px-6 py-4 text-gray-700">2020</td>
                </tr>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="border border-gray-300 px-6 py-4 font-medium text-gray-900">Incendios</td>
                  <td className="border border-gray-300 px-6 py-4 text-gray-700">CONAFOR / Sistema Nacional de Información de Incendios</td>
                  <td className="border border-gray-300 px-6 py-4 text-gray-700">2015-2020</td>
                </tr>
                <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                  <td className="border border-gray-300 px-6 py-4 font-medium text-gray-900">Importancia para la Conservación</td>
                  <td className="border border-gray-300 px-6 py-4 text-gray-700">CONABIO-GEOWEB-Áreas Naturales Protegidas</td>
                  <td className="border border-gray-300 px-6 py-4 text-gray-700">2020</td>
                </tr>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="border border-gray-300 px-6 py-4 font-medium text-gray-900">Densidad de Población</td>
                  <td className="border border-gray-300 px-6 py-4 text-gray-700">INEGI / Censo de Población y Vivienda</td>
                  <td className="border border-gray-300 px-6 py-4 text-gray-700">2020</td>
                </tr>
                <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                  <td className="border border-gray-300 px-6 py-4 font-medium text-gray-900">Rezago Social</td>
                  <td className="border border-gray-300 px-6 py-4 text-gray-700">CONEVAL / Índice de Rezago Social</td>
                  <td className="border border-gray-300 px-6 py-4 text-gray-700">2020</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>



        {/* Normalización de Datos */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <Calculator className="h-6 w-6 text-indigo-600" />
            <h2 className="text-2xl font-bold text-gray-900">3. Normalización de Datos</h2>
          </div>
          
          <div className="prose max-w-none text-gray-700 mb-6">
            <p className="text-lg mb-4">
              Para permitir la comparación entre indicadores con diferentes unidades y escalas, se aplica un proceso 
              de normalización utilizando la fórmula:
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="font-mono text-lg text-center mb-4 bg-white p-4 rounded border">
              Valor_normalizado = (Valor_actual - Valor_mínimo) / (Valor_máximo - Valor_mínimo)
            </div>
            <p className="text-center text-gray-700">
              Este proceso transforma todos los valores a una escala de 0 a 1.
            </p>
          </div>
        </div>

        {/* Fórmula Matemática */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <BarChart3 className="h-6 w-6 text-orange-600" />
            <h2 className="text-2xl font-bold text-gray-900">4. Índice de Priorización (IP)</h2>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cálculo del Índice de Prioridad</h3>
            <p className="text-gray-700 mb-4">
              El índice de prioridad se calcula como la suma ponderada de los indicadores normalizados:
            </p>
            <div className="font-mono text-lg text-center mb-2 bg-white p-4 rounded border">
              Índice_Prioridad = Σ(Valor_normalizado_i × Ponderación_i)
            </div>
            <p className="text-center text-gray-600 text-sm mb-4">
              Donde <em>i</em> representa cada uno de los 8 indicadores considerados.
            </p>
          </div>
        </div>

        {/* Niveles de Prioridad */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <Droplets className="h-6 w-6 text-cyan-600" />
            <h2 className="text-2xl font-bold text-gray-900">5. Clasificación</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {[
            { nivel: "Muy Alta", color: "bg-red-600", descripcion: "Intervención inmediata" },
            { nivel: "Alta", color: "bg-orange-500", descripcion: "Intervención prioritaria" },
            { nivel: "Media", color: "bg-yellow-500", descripcion: "Intervención programada" },
            { nivel: "Baja", color: "bg-[#a6d96a]", descripcion: "Monitoreo regular" },
            { nivel: "Muy Baja", color: "bg-[#1a9641]", descripcion: "Conservación" }].
            map((prioridad, index) =>
            <div key={index} className="text-center">
                <div className={`${prioridad.color} text-white p-4 rounded-lg mb-2`}>
                  <div className="font-bold text-lg">{prioridad.nivel}</div>
                </div>
                <p className="text-sm text-gray-600">{prioridad.descripcion}</p>
              </div>
            )}
          </div>
        </div>

        {/* Referencias y Fuentes */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center space-x-3 mb-6">
            <FileText className="h-6 w-6 text-gray-600" />
            <h2 className="text-2xl font-bold text-gray-900">Referencias y Fuentes</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Fuentes de Datos</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• INEGI - Instituto Nacional de Estadística y Geografía</li>
                <li>• CONABIO - Comisión Nacional para el Conocimiento y Uso de la Biodiversidad</li>
                <li>• SMN - Servicio Meteorológico Nacional</li>
                <li>• CONAFOR - Comisión Nacional Forestal</li>
                <li>• SEMARNAT - Secretaría de Medio Ambiente y Recursos Naturales</li>
              </ul>
            </div>
          </div>
          
          <div className="mt-6 pt-6 border-t border-gray-200">
            



          </div>
        </div>
      </div>
    </div>);

};

export default Metodologia;